import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import { range } from 'underscore';

import { UserProfilePicture } from '~src/app/content/UserProfilePicture';
import { Messages } from '~src/codegen/Localize';
import { TelemetryEvents } from '~src/common/telemetryEvents/TelemetryEvents';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { searchTelemetry } from '~src/search/components/SearchTelemetry';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { Keys } from '~src/ts/helpers/Keys';

import { IQuickLinksContentTypes, OmniBoxItemType } from '../models/Types';
import { SearchService } from '../services/SearchService';
import { formatAsSearchUrl, getContentTypeUrl, getUserFilteredSearchUrl, OmniboxTelemetryEventSource, OmniboxViewModel } from '../viewModels/OmniboxViewModel';

const emptySearch = '?' + ContentFiltersKeys.search + '=';

export function QuickLinks(viewModel: OmniboxViewModel): JSX.Element {
    const quickLinksContentTypeMap: Record<string, IQuickLinksContentTypes> = {
        [OmniBoxItemType.views]:  {
            name: Messages.views(),
            getPath: (urls) => urls.searchViewsUrl() + emptySearch,
            icon: 'view_18px-large.svg',
        },
        [OmniBoxItemType.workbooks]:  {
            name: Messages.workbooks(),
            getPath: (urls) => urls.searchWorkbooksUrl() + emptySearch,
            icon: 'workbook_18px-large.svg',
        },
        [OmniBoxItemType.metrics]:  {
            name: Messages.metrics(),
            getPath: (urls) => urls.searchMetricsUrl() + emptySearch,
            icon: 'metrics_18px-large.svg',
        },
        [OmniBoxItemType.collections]:  {
            name: Messages.collections(),
            getPath: (urls) => urls.allCollectionsUrl(),
            icon: 'collection_18px-large.svg',
        },
        [OmniBoxItemType.datasources]:  {
            name: Messages.datasources(),
            getPath: (urls) => urls.searchDatasourcesUrl() + emptySearch,
            icon: 'datasource_18px-large.svg',
        }
    };

    function renderQuickLinks(): JSX.Element {
        return <div>
                {shouldRenderRecentlyVisited() ?
                    <div className='tb-quicklinks-section' id='quicklinks-recentsearches-section'>
                        {renderHeader(Messages.recentlyVisited())}
                        <div className='tb-quicklinks-list'>{renderRecentlyVisitedList()}</div>
                    </div> :
                    <></>}
                <div className={'tb-quicklinks-section' + (shouldRenderRecentlyVisited() ? ' tb-quicklinks-section-divider' : '')}
                    id='quicklinks-content-section'>
                    {renderHeader(Messages.contentType())}
                    <div className='tb-quicklinks-row'>{renderContentTypes()}</div>
                </div>
                <div className={'tb-quicklinks-section tb-quicklinks-section-divider'+(!viewModel.quickLinksResultReady?' tb-loading':'')+(viewModel.quickLinksResultReady && viewModel.quickLinksResult.length===0?' tb-empty':'')}
                    id='quicklinks-owner-section'>
                {FeatureFlags.searchQuickLinks && !SearchService.instance.Server.isLimitedUserVisibilityEnforced() ?
                    (viewModel.quickLinksResultReady ?
                        (viewModel.quickLinksResult.length > 0 ?
                            <>
                                {renderHeader(Messages.owner())}
                                <div className='tb-quicklinks-row'>{renderOwners()}</div>
                            </> :
                            <></>) :
                        <div className='tb-quicklinks-pending-spinner' style={{height: '134px'}}>
                            <ActivitySpinnerWidget
                                spinnerSize={ActivitySpinnerSize.Small}
                                testId={'omnibox-pending'}
                            />
                        </div>) :
                    <></>}
                    </div>
            </div>;
    }

    function shouldRenderRecentlyVisited(): boolean {
        return FeatureFlags.recentSearches && viewModel.getRecentSearches().length > 0;
    }

    function renderHeader(title: string): JSX.Element {
        return <span className={'tb-quicklinks-header'}>
                {title}
            </span>;
    }

    function renderRecentlyVisitedList(): JSX.Element[] {
    const focus = (id: string, highlight: boolean) => {
            if (highlight) {
                document.querySelector<HTMLAnchorElement>(`#${id}`).className += ' tb-quicklinks-list-item-hover-focus';
                document.querySelector<HTMLAnchorElement>(`#${id}`).querySelector<HTMLButtonElement>('.tb-quicklinks-list-item-button').style.visibility = 'visible';
            } else {
                document.querySelector<HTMLAnchorElement>(`#${id}`).className =
                    document.querySelector<HTMLAnchorElement>(`#${id}`).className.replace(' tb-quicklinks-list-item-hover-focus', '');
                    document.querySelector<HTMLAnchorElement>(`#${id}`).querySelector<HTMLButtonElement>('.tb-quicklinks-list-item-button').style.visibility = 'hidden';
            }
        };
        return viewModel.getRecentSearches().map((searchText, index) => {
            const id = `quickLinks-recentSearch_${index}`;
            return <div className='tb-quicklinks-list-item' key={searchText} id={id}>
                    <a role='menuitem' onKeyDown={(e) => keydownHandler(e)} key={searchText + '-0'} href={formatAsSearchUrl(searchText)}
                        id={id + '-0'} className='tb-quicklinks-list-item-link' onFocus={() => focus(id, true)} onBlur={() => focus(id, false)}
                        onMouseEnter={() => focus(id, true)} onMouseLeave={() => focus(id, false)} onClick={() => viewModel.addSearchToRecentSearches(searchText)}>
                        <img src={imgPath('recent_18px-large.svg')} alt=''/>
                        <span style={{ marginLeft: '6px', display: 'block', fontSize: '14px' }}>{searchText}</span>
                    </a>
                    <button className='tb-quicklinks-list-item-button' onClick={() => viewModel.removeSearchFromRecentSearches(searchText)}
                        key={searchText + '-1'} onMouseEnter={() => focus(id, true)} onMouseLeave={() => focus(id, false)}/>
                </div>;
        });
    }

    function renderContentTypes(): JSX.Element[] {
        const sections = viewModel.getQuickLinksSections();
        const contentTypes = Object.keys(quickLinksContentTypeMap).filter(contentType => !(contentType === OmniBoxItemType.metrics && !FeatureFlags.metricsSpaceEnabled) &&
            !(contentType === OmniBoxItemType.collections && !FeatureFlags.collectionsEnabled));
        return range(0, 5).map((index: number) => {
            if (index < contentTypes.length) {
                const contentType = contentTypes[index];
                const content = quickLinksContentTypeMap[contentType];
                const id = `quickLinks-content-${index}`;
                return <a role='menuitem' onKeyDown={(e) => keydownHandler(e)} key={content.name} href={getContentTypeUrl(content)}
                        className='tb-quicklinks-highlight' id={id} onClick={() => onContentTypeLinkClicked(contentTypes.length, index)}>
                        <div key={content.name} className='tb-quicklinks-highlight-border'>
                            <img src={imgPath(content.icon)} alt=''/>
                        </div>
                        <span style={{ display: 'block', marginTop: '6px', fontSize: '12px' }}>{content.name}</span>
                    </a>;
            } else {
                return <div key={'content-placeholder' + index} className='tb-quicklinks-placeholder' ></div>;
            }
        });
    }

    function onContentTypeLinkClicked(length: number, index: number) {
        viewModel.cancelSearch();
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: OmniBoxItemType.contentTypeItem,
            seeAll: true,
            index: index,
            total: length,
            searchTerm: viewModel.searchText,
            contentId: '-1'
        });
    }

    function renderOwners(): JSX.Element[] {
        const sections = viewModel.getQuickLinksSections();
        return range(0, 5).map((index: number) => {
            if (index < viewModel.quickLinksResult.length) {
                const user = viewModel.quickLinksResult[index];
                const id = `quickLinks-user-${index}`;
                return <a role='menuitem' onKeyDown={(e) => keydownHandler(e)} key={user.id} href={getUserFilteredSearchUrl(user)}
                        className='tb-quicklinks-highlight' id={id} onClick={() => onOwnerClicked(viewModel.quickLinksResult.length, index)}>
                        <div key={user.id}>
                            <UserProfilePicture profileDisplayName={user.displayName} imageUrl={user.userImageUrl}
                                imageSize={40} fontSize={16} divStyle={{ marginRight: '8px' }} />
                        </div>
                        <span style={{ display: 'block', marginTop: '6px', fontSize: '12px' }}>{user.displayName}</span>
                </a>;
            } else {
                return <div key={'owner-placeholder' + index} className='tb-quicklinks-placeholder' ></div>;
            }
        });
    }

    function onOwnerClicked(length: number, index: number){
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: OmniBoxItemType.ownerItem,
            seeAll: true,
            index: index,
            total: length,
            searchTerm: viewModel.searchText,
            contentId: '-1'
        });
    }

    function keydownHandler(event: React.KeyboardEvent<HTMLAnchorElement>): void {
        switch (event.which) {
            case Keys.Escape:
                document.querySelector<HTMLAnchorElement>('#search-omnibox').focus();
                break;
            case Keys.Tab:
                event.preventDefault();
                navigateLeftOrRight(event);
                break;
            case Keys.ArrowRight:
                navigateLeftOrRight(event);
                break;
            case Keys.ArrowLeft:
                navigateLeftOrRight(event);
                break;
            case Keys.ArrowDown:
                navigateUpOrDown(event);
                break;
            case Keys.ArrowUp:
                navigateUpOrDown(event);
                break;
            case Keys.Delete:
                deleteEntry(event);
                break;
        }
    }

    function navigateLeftOrRight(event: React.KeyboardEvent<HTMLAnchorElement>) {
        const links = document.querySelectorAll<HTMLDivElement>('.tb-quicklinks a');
        let index: number = Array.prototype.indexOf.call(links, event.target);
        if (event.keyCode === Keys.ArrowRight || event.keyCode === Keys.Tab) {
          index = (index === links.length - 1) ? 0 : index + 1;
        } else if (event.keyCode === Keys.ArrowLeft) {
          index = (index === 0) ? links.length - 1 : index - 1;
        }
        links[index].focus();
    }

    function navigateUpOrDown(event: React.KeyboardEvent<HTMLAnchorElement>) {
        event.preventDefault();
        const sections = viewModel.getQuickLinksSections();
        const id = event.currentTarget.id;
        const sectionName = id.split('-')[1];
        let index = sections.indexOf(sectionName);
        if (event.keyCode === Keys.ArrowDown) {
            index = (index === sections.length - 1) ? 0 : index + 1;
        } else if (event.keyCode === Keys.ArrowUp) {
            index = (index === 0) ? sections.length - 1 : index - 1;
        }
        if (index === 0) {
            document.querySelector<HTMLAnchorElement>('#search-omnibox').focus();
        }
        else {
            const target = id.replace(sectionName, sections[index]);
             if (document.querySelector<HTMLAnchorElement>('#' + target)) {
                document.querySelector<HTMLAnchorElement>('#' + target).focus();
             } else {
                // when the sections do not have the same number of elements then it will go to the first item of the next row
                const number = id.split('-')[2];
                document.querySelector<HTMLAnchorElement>('#' + target.replace(number, '0')).focus();
             }

        }
    }

    function deleteEntry(event: React.KeyboardEvent) {
        const id = event.currentTarget.id;
        const sectionName = id.split('-')[1];
        if (sectionName.includes('recentSearch')) {
            const searchIndex = +sectionName.split('_')[1];
            viewModel.removeSearchFromRecentSearches(viewModel.getRecentSearches()[searchIndex]);
            document.querySelector<HTMLAnchorElement>('#search-omnibox').focus();
        }
    }

    return <span style={{width: 'inherit'}} >
        <ul className='tb-quicklinks' role='menu' tabIndex={0}>
            {renderQuickLinks()}
        </ul>
    </span>;
}
